import { createTournament, preset, reduce, type ActionBody, type Tournament } from '@airmail/engine';
import { db, newId, saveTournament } from '@/store/db';
import { phaseConfigsFor, DEFAULT_MODE_OPTIONS } from '@/lib/modes';
import { getLang, t as uebersetze, woerterbuch, type Lang } from '@/i18n';

/**
 * Einführungs-Tour: führt einmal durch die App — an einem Demo-Turnier, damit
 * niemand an echten Daten herumklickt. Zustand liegt im Modul (über Routen
 * hinweg), Erledigt-Merker im localStorage.
 */
export interface TourStep {
  /** Zielroute; `{t}` = Demo-Turnier-ID */
  route: string;
  /** data-tour-Wert des hervorgehobenen Elements; ohne → Karte mittig */
  target?: string;
  title: string;
  text: string;
}

export const TOUR_STEPS: TourStep[] = [
  {
    route: '/',
    target: 'list-new',
    title: 'Willkommen bei Airmail',
    text: 'Hier liegen deine Turniere. „Neues Turnier“ führt in fünf Schritten durch Name, Modus, Regeln, Felder und Teams; daneben „Saisons“ für Spieltage über das Jahr. Für diese Tour habe ich ein Demo-Turnier mit 8 Teams angelegt — du kannst es danach löschen.',
  },
  {
    route: '/',
    target: 'list-account',
    title: 'Dein Konto',
    text: 'Angemeldet werden deine Turniere und Saisons automatisch in der Cloud gesichert, sobald Internet da ist. Damit kannst du sie auf jedem Gerät öffnen, Helfer per Link dazuholen, Mitorganisatoren einladen und die Online-Anmeldung nutzen. Ohne Netz läuft alles lokal weiter.',
  },
  {
    route: '/t/{t}/teams',
    target: 'nav-teams',
    title: 'Teams',
    text: 'Teams kommen per Einfügen aus Excel, per Datei, per Online-Anmeldung oder einzeln. Die Liste zeigt Verein oder Mannschaft, Setzung, Startgeld und Check-in — und die Antworten auf deine Zusatzfragen (Essen, Übernachtung …) als eigene Spalten mit Summe.',
  },
  {
    route: '/t/{t}/teams',
    target: 'teams-checkin',
    title: 'Check-in am Turniertag',
    text: 'Häkchen in der Liste oder der Check-in-Modus mit großen Kacheln für die Tür. Im Liga-Modus zählt der Check-in doppelt: Ausgelost werden nur eingecheckte Paare, und wer später kommt, bekommt mit dem Häkchen sofort seine Spiele.',
  },
  {
    route: '/t/{t}/teams',
    target: 'teams-import',
    title: 'Importieren',
    text: 'Der Import erkennt Tab, Semikolon und Komma, Überschriften und mehrere Schreibweisen: „Team: Spieler; Spieler“, „Team; Spieler; Spieler“ oder nur „Anna / Max“. Im Liga-Modus reicht „Ceva; Anna; Luca“ je Zeile — oder du lädst den Kader der Saison, dann bleiben die Namen gleich. Excel-Spalten lassen sich zuordnen; Duplikate und fehlende Partner werden gemeldet.',
  },
  {
    route: '/t/{t}/vorrunde',
    target: 'nav-vorrunde',
    title: 'Auslosung',
    text: '„Auslosen“ verteilt die Teams nach Setzung auf Gruppen und trennt Vereinskollegen, wenn möglich. Teams per Drag & Drop tauschen, dann einfrieren. Im Liga-Modus entstehen hier die Spiele Paar gegen Paar der anderen Mannschaften — nur für eingecheckte Paare, ohne die in der Saison schon gespielten; auf Wunsch in Leistungsgruppen (alle Einser zusammen, alle Zweier …).',
  },
  {
    route: '/t/{t}/spiele',
    target: 'nav-spiele',
    title: 'Spiele',
    text: 'Die Turnierzentrale: links die Felder, rechts die Warteschlange. Freie Felder werden automatisch belegt, dabei bekommt kein Team Dauereinsatz. Unter „Beendet“ stehen die letzten Ergebnisse — antippen für eine Korrektur. Oben „Rückgängig“ und „Wieder vorwärts“. Sind alle Spiele gewertet, steht hier oben „Phase abschließen“ bzw. „Turnier beenden“.',
  },
  {
    route: '/t/{t}/spiele',
    target: 'spiele-felder',
    title: 'Ergebnis eintragen',
    text: '„Ergebnis“ öffnet den Ziffernblock: erste Zahl, Tab, zweite Zahl, Enter — wer unter 21 bleibt, dessen Gegner bekommt automatisch die 21. Ist ein Team nicht da, tippst du unter „Nicht angetreten“ auf „… fehlt“: Der Gegner gewinnt kampflos 21:0, in den Listen als w.o. Aufgabe und Zeitlimit stehen daneben.',
  },
  {
    route: '/t/{t}/tabelle',
    target: 'nav-tabelle',
    title: 'Tabelle',
    text: 'Live-Tabellen je Gruppe mit Begründung bei Gleichstand (direkter Vergleich, Punktdifferenz …) und einer Gesamttabelle über alle Gruppen. Im Liga-Modus zusätzlich Mannschafts-, Paar- und Spielerwertung und „In Saison übernehmen“: Die Saison summiert die Spieltage, zeigt in der Kreuztabelle, welche Begegnungen noch offen sind, und merkt sich den Kader.',
  },
  {
    route: '/t/{t}/ko',
    target: 'nav-ko',
    title: 'KO-Runde',
    text: 'Nach der Gruppenphase rücken die Besten ins Bracket, Gruppengegner treffen sich möglichst spät wieder. Spiel um Platz 3 optional. Wer lieber Runde für Runde entscheidet, wie viele weiterkommen, nimmt den Modus „KO Runde für Runde“.',
  },
  {
    route: '/t/{t}/felder',
    target: 'nav-felder',
    title: 'Felder',
    text: 'Felder benennen, sperren, auf Plätze verteilen (Halle A/B, Piazza/Sportplatz) und Prioritäten setzen — die Auto-Zuweisung hält sich daran, und ein Helfer lässt sich auf einen Platz festlegen.',
  },
  {
    route: '/t/{t}/beamer',
    target: 'nav-beamer',
    title: 'Beamer',
    text: 'Die Anzeige für die Halle: Felder mit Spielstand, Tabellen, KO und am Ende das Siegertreppchen, alle 20 Sekunden wechselnd. Ein Endstand bleibt noch stehen, das nächste Spiel steht als Aufruf oben. „Eigenes Fenster“ öffnet den Beamer für den Fernseher — dort Vollbild, er folgt diesem Fenster live. Unten rechts der QR-Code für die Zuschauer.',
  },
  {
    route: '/t/{t}/export',
    target: 'nav-export',
    title: 'Export & Druck',
    text: 'Aushänge für die Wand: Spielplan je Gruppe, Tabellen, Bracket, Teamliste. Etiketten für den Bogen oder den Dymo (Begegnung je Spiel) und Spielzettel. Dazu Backup als Datei, Ergebnisse für Excel und der Austausch von Ergebnissen per Datei, wenn zwei Plätze kein Netz haben.',
  },
  {
    route: '/t/{t}/einstellungen',
    target: 'nav-einstellungen',
    title: 'Einstellungen',
    text: 'Hier stellst du Regeln (Punktziel, Bust, Innings, Zeitlimit) und Modus ein — und alles rund um Cloud, Helfer und Anmeldung. Die nächsten drei Schritte zeigen das im Einzelnen.',
  },
  {
    route: '/t/{t}/einstellungen',
    target: 'settings-sync',
    title: 'Helfer-Link teilen',
    text: 'Cloud-Sync sichert das Turnier in deinem Konto. Darunter gibst du je Helfer einen eigenen Link aus: Name eintippen, optional den Platz wählen, Link zeigen und abscannen lassen. Er darf nur Spiele starten, Spielstände und Ergebnisse eintragen — das prüft der Server. Einen Link zurückzuziehen trifft nur diesen einen Helfer. Für dein zweites eigenes Gerät gibt es darunter den Leitungs-Link.',
  },
  {
    route: '/t/{t}/einstellungen',
    target: 'settings-members',
    title: 'Mitorganisatoren',
    text: 'Kollegen aus dem Orga-Team per E-Mail einladen. Sie melden sich mit dieser Adresse an und haben das Turnier dauerhaft in ihrem eigenen Konto — löschen und weitere einladen bleibt beim Eigentümer.',
  },
  {
    route: '/t/{t}/einstellungen',
    target: 'settings-registration',
    title: 'Anmelde-Link erstellen',
    text: '„Anmeldung einrichten“: Anmeldeschluss, Teamgröße, optional Teilnehmerlimit mit Warteliste, ein Hinweistext — und eigene Zusatzfragen (Essen ja/nein, Übernachtungen, Freitext), als Vorlage im Konto speicherbar. Dann Link oder QR-Code teilen, Plakat oder Flyer drucken. Die Anmeldungen holst du unter „Teams → Anmeldungen“ mit einem Klick als Teams herein, Antworten inklusive.',
  },
  {
    route: '/t/{t}/einstellungen',
    target: 'hdr-backup',
    title: 'Sicher ist sicher',
    text: '„Rückgängig“ nimmt die letzte Aktion zurück, „Wieder vorwärts“ holt sie zurück, „Backup“ speichert das Turnier als Datei. Ohne Internet läuft alles weiter; die Cloud holt den Stand nach, sobald Netz da ist. Am Handy findest du das alles unter „Mehr“ in der Leiste unten.',
  },
  {
    route: '/',
    title: 'Fertig',
    text: 'Das Demo-Turnier kannst du in der Liste löschen oder zum Üben durchspielen. Die Einführung findest du jederzeit wieder über „Einführung“. Viel Spaß beim Turnier!',
  },
];

/**
 * Kurze Einführung für **Helfer** (docs/notes/helfer.md).
 *
 * Getrennt von der großen Tour, und zwar aus einem handfesten Grund: Die große
 * legt ein Demo-Turnier an und beginnt in der Turnierliste — dort steht „Neues
 * Turnier". Für einen Helfer wäre der Einführungsknopf damit genau die Tür,
 * die ihm sonst überall verschlossen ist (Daniel: „so geht es zurück aufs
 * hauptmenu"). Diese hier bleibt im laufenden Turnier und legt nichts an.
 *
 * Fünf Schritte, mehr braucht sein Job nicht. Angeboten wird sie einmal von
 * selbst, wenn ein Helfer das Turnier zum ersten Mal öffnet (`offerHelferTour`).
 */
export const HELFER_STEPS: TourStep[] = [
  {
    route: '/t/{t}/spiele',
    target: 'nav-spiele',
    title: 'Willkommen',
    text: 'Du hilfst bei diesem Turnier aus. Zwei Seiten hast du: „Spiele“ zum Eintragen und „Tabelle“ zum Nachschauen. Alles andere macht die Turnierleitung — du kannst hier nichts kaputt machen.',
  },
  {
    route: '/t/{t}/spiele',
    target: 'spiele-felder',
    title: 'Spiel starten, Ergebnis eintragen',
    text: 'Oben stehen die Felder. Ist eins frei, holt „Spiel starten“ das nächste Spiel darauf. Läuft es, öffnet „Ergebnis“ den Ziffernblock: erste Zahl, Tab, zweite Zahl, Enter — der Sieger bekommt automatisch die 21. Zwischenstände kannst du mit „Spielstand“ mitschreiben, dann sehen Beamer und Zuschauer sie live.',
  },
  {
    route: '/t/{t}/spiele',
    target: 'spiele-warteschlange',
    title: 'Nicht angetreten? Vertippt?',
    text: 'Fehlt ein Team, tippst du im Ergebnisdialog unter „Nicht angetreten“ auf „… fehlt“ — der Gegner gewinnt kampflos 21:0. Vertippt? Das beendete Spiel unter „Beendet“ antippen und das richtige Ergebnis eintragen; es gilt immer das zuletzt Eingetragene. Rechts siehst du, was als Nächstes drankommt.',
  },
  {
    route: '/t/{t}/tabelle',
    target: 'nav-tabelle',
    title: 'Tabelle',
    text: 'Hier kannst du Spielern den Stand zeigen. Sie aktualisiert sich von selbst, sobald Ergebnisse hereinkommen — von dir, von der Leitung oder von einem anderen Helfer.',
  },
  {
    route: '/t/{t}/spiele',
    title: 'Handy und Funkloch',
    text: 'Am Handy findest du „Spiele“ und „Tabelle“ in der Leiste unten. Ohne Internet läuft alles weiter — deine Eingaben gehen von selbst raus, sobald wieder Netz da ist. Diese Einführung findest du jederzeit wieder unter „Einführung“.',
  },
];

/** Erledigt-Merker je Tour: Leitung und Helfer werden jeweils genau einmal gefragt */
const DONE_KEYS = { leitung: 'airmail.tourDone', helfer: 'airmail.helferTourDone' } as const;
export type TourKind = keyof typeof DONE_KEYS;

/**
 * Das Demo-Turnier in der Sprache, in der die Einführung gestartet wird. Ein
 * italienischer Verein soll keine schwäbischen Vereine und keine „Werfer“
 * sehen — die Namen sind Teil der Vorstellung (die Bildschirmabzüge der
 * Willkommensseite zeigen genau dieses Turnier, docs/notes/willkommen.md).
 *
 * Deutsch bleibt Wort für Wort, wie es war: Die Browser-Läufe (e2e) kennen
 * „Bag Brothers“, „CC Ulm“ und „Demo-Turnier“ beim Namen. Feldnamen kommen
 * aus dem Wörterbuch („Feld {n}“ → Court/Campo).
 */
type DemoTeam = [name: string, spielerA: string, spielerB: string, verein: string];
const DEMO: Record<Lang, { name: string; teams: DemoTeam[] }> = {
  de: {
    name: 'Demo-Turnier',
    teams: [
      ['Bag Brothers', 'Anna Rossi', 'Luca Bianchi', 'CC Ulm'],
      ['Hole in None', 'Lisa Schmitt', 'Tom Berger', 'CC Ulm'],
      ['Airmail Crew', 'Gina Russo', 'Sara Conti', 'Neu-Ulm'],
      ['Die Werfer', 'Peter Lang', 'Harald Vogt', 'Neu-Ulm'],
      ['Sackflug', 'Vera Klein', 'Paula Roth', 'Senden'],
      ['Brettgeflüster', 'Marco Neri', 'Elena Costa', 'Senden'],
      ['Loch Ness', 'Kai Haas', 'Nina Lang', 'Laupheim'],
      ['Cornholio', 'Max Müller', 'Daniel Weber', 'Laupheim'],
    ],
  },
  en: {
    name: 'Demo tournament',
    teams: [
      ['Bag Brothers', 'Anna Ross', 'Luke White', 'Riverside CC'],
      ['Hole in None', 'Lisa Smith', 'Tom Baker', 'Riverside CC'],
      ['Airmail Crew', 'Gina Russell', 'Sarah Cole', 'Oakfield'],
      ['Board Walkers', 'Peter Long', 'Harry Fox', 'Oakfield'],
      ['Slide Rule', 'Vera Little', 'Paula Reed', 'Millbrook'],
      ['Board Whisperers', 'Mark Hill', 'Ellen Cross', 'Millbrook'],
      ['Loch Ness', 'Kai Hayes', 'Nina Long', 'Kingsbridge'],
      ['Cornholio', 'Max Miller', 'Daniel Webb', 'Kingsbridge'],
    ],
  },
  // Piemont und Lombardei: Bocciofila, Piazza, Paese — dort spielt die Zielgruppe
  it: {
    name: 'Torneo demo',
    teams: [
      ['Sacco Volante', 'Anna Rossi', 'Luca Bianchi', 'Bocciofila Alba'],
      ['Buco Perfetto', 'Giulia Ferrari', 'Matteo Conti', 'Bocciofila Alba'],
      ['Airmail Crew', 'Chiara Russo', 'Sara Galli', 'Piazza Castello'],
      ['I Lanciatori', 'Marco Neri', 'Paolo Verdi', 'Piazza Castello'],
      ['Tiro Libero', 'Elena Costa', 'Francesca Ricci', 'Ceva'],
      ['Quattro Sacchi', 'Davide Moretti', 'Andrea Fontana', 'Ceva'],
      ['Centro Tavola', 'Laura Romano', 'Silvia Greco', 'Mondovì'],
      ['Cornholio', 'Giorgio Rinaldi', 'Stefano Colombo', 'Mondovì'],
    ],
  },
};
/** Deutscher Name — und alle Namen, unter denen ein Demo-Turnier auf dem Gerät liegen kann */
export const DEMO_NAME = DEMO.de.name;
export const DEMO_NAMES: readonly string[] = Object.values(DEMO).map((d) => d.name);

export interface TourState {
  active: boolean;
  index: number;
  tournamentId: string | null;
  /** welche Schrittfolge läuft — die große oder die für Helfer */
  kind: TourKind;
  /** Turnier, für das gerade die Helfer-Einführung angeboten wird (Einladung unten im Bild) */
  einladung: string | null;
}
let state: TourState = { active: false, index: 0, tournamentId: null, kind: 'leitung', einladung: null };

/** Die Schritte der gerade laufenden Tour */
export function steps(s: TourState = state): TourStep[] {
  return s.kind === 'helfer' ? HELFER_STEPS : TOUR_STEPS;
}
const listeners = new Set<(s: TourState) => void>();
function set(next: Partial<TourState>): void {
  state = { ...state, ...next };
  for (const l of listeners) l(state);
}
export function tourState(): TourState {
  return state;
}
export function subscribeTour(fn: (s: TourState) => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function tourDone(kind: TourKind = 'leitung'): boolean {
  try {
    return !!localStorage.getItem(DONE_KEYS[kind]);
  } catch {
    return true;
  }
}
export function markTourDone(how: 'done' | 'skipped', kind: TourKind = 'leitung'): void {
  try {
    localStorage.setItem(DONE_KEYS[kind], how);
  } catch {
    /* egal */
  }
}

/**
 * Helfer-Einführung einmal anbieten (Daniel: „sollten nur beim ersten Mal
 * gefragt werden“). Die Einladung erscheint unten im Bild, sobald ein Helfer
 * das Turnier öffnet — „Später“ oder „Los geht’s“, beides merkt sich das Gerät.
 */
export function offerHelferTour(tournamentId: string): void {
  if (state.active || state.einladung || tourDone('helfer')) return;
  set({ einladung: tournamentId });
}
export function dismissHelferInvite(): void {
  markTourDone('skipped', 'helfer');
  set({ einladung: null });
}

/**
 * Demo-Turnier: 8 Teams, 2 Felder, Gruppen → KO — in der aktuellen Sprache
 * (`getLang()`). Liegt schon eins auf dem Gerät, in welcher Sprache auch
 * immer, wird es wiederverwendet: Wer die Sprache wechselt und die Einführung
 * noch einmal startet, soll nicht ein zweites Demo-Turnier in der Liste finden.
 */
export async function ensureDemoTournament(): Promise<string> {
  const existing = (await db.tournaments.toArray()).find((r) => DEMO_NAMES.includes(r.name));
  if (existing) return existing.id;
  const demo = DEMO[getLang()];
  // Ort, Bewerb und Felder kommen aus dem Wörterbuch — das lädt EN/IT erst bei Bedarf, also hier abwarten
  if (getLang() !== 'de') await woerterbuch().catch(() => undefined);
  const id = newId();
  let t: Tournament = createTournament({ id, name: demo.name, seed: id, createdAt: Date.now() });
  const run = (body: ActionBody) => {
    const r = reduce(t, { ...body, at: Date.now() });
    if (r.ok) t = r.state;
  };
  run({ type: 'tournament.update', patch: { date: new Date().toISOString().slice(0, 10), location: uebersetze('Zum Ausprobieren') } });
  run({ type: 'competition.add', name: uebersetze('Doubles'), ruleSet: preset('tournament') });
  const comp = t.competitions[0];
  if (!comp) throw new Error('kein Bewerb');
  run({ type: 'court.add', name: uebersetze('Feld {n}', { n: 1 }) });
  run({ type: 'court.add', name: uebersetze('Feld {n}', { n: 2 }) });
  for (const cfg of phaseConfigsFor('groups_ko', DEFAULT_MODE_OPTIONS)) run({ type: 'phase.add', competitionId: comp.id, config: cfg });
  demo.teams.forEach(([name, a, b, club], i) => {
    const [af, al] = a.split(' ') as [string, string];
    const [bf, bl] = b.split(' ') as [string, string];
    run({
      type: 'team.add',
      competitionId: comp.id,
      team: {
        name,
        club,
        seed: i + 1,
        paid: i % 3 !== 0,
        players: [
          { firstName: af, lastName: al },
          { firstName: bf, lastName: bl },
        ],
      },
    });
  });
  await saveTournament(t);
  return id;
}

export async function startTour(): Promise<void> {
  const tournamentId = await ensureDemoTournament();
  set({ active: true, index: 0, tournamentId, kind: 'leitung' });
}

/** Helfer-Tour: im laufenden Turnier, ohne Demo-Turnier und ohne Umweg über die Turnierliste */
export function startHelferTour(tournamentId: string): void {
  set({ active: true, index: 0, tournamentId, kind: 'helfer', einladung: null });
}
export function stopTour(how: 'done' | 'skipped' = 'skipped'): void {
  markTourDone(how, state.kind);
  set({ active: false, index: 0 });
}
export function nextStep(): void {
  if (state.index >= steps().length - 1) return stopTour('done');
  set({ index: state.index + 1 });
}
export function prevStep(): void {
  if (state.index > 0) set({ index: state.index - 1 });
}
